import React from 'react';

import ScrollLock from 'react-scrolllock';
import { styled } from '@components-shared/src/stitches';
import { MINIMIZED_PLAYER_HEIGHT } from '@components/AudioPlayerMobile/MinimizedPlayer';
import { getSearchPageResults } from '@components/Explore/helpers';
import { LoadingBars } from '@components/LoadingBars/LoadingBars';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { MOBILE_NAV_HEIGHT } from '@components/main-components/mobile-nav/constants';
import {
  DividerContainer,
  LoadingContainer,
  ScrollableContainer,
  SearchDivider,
  SearchResultsContainer,
  SearchRow,
} from '@components/main-components/search/SearchResults';
import type {
  SearchResultArtistFragment,
  SearchResultCollectorFragment,
  SearchResultReleaseFragment,
  SearchResultShelfFragment,
} from '@graphql/reactQuery';
import { useVisualViewport } from '@hooks/useVisualViewport';
import { useAudioController } from '@store/audio/AudioController';
import { webappUriPath } from '@utils/url';
import { SearchHistoryRow } from './SearchHistory/SearchHistoryRow';
import { useSearchHistoryStore } from './SearchHistory/useSearchHistoryStore';
import { ArtistRow, CollectorRow, PlaylistRow, ReleaseRow } from './shared';
import { splitSlots } from './splitSlots';

const slots = 16;

const SearchForRow = ({
  searchText,
  onSearchClick,
}: {
  searchText: string;
  onSearchClick: () => void;
}) => {
  return (
    <>
      {searchText !== '' && (
        <AccessibleLink
          pathnameUri={webappUriPath(`/search`)}
          onClick={ev => {
            ev.stopPropagation();
            ev.preventDefault();

            onSearchClick();

            getSearchPageResults({ inputText: searchText, useRouter: false });
          }}
        >
          <div className="bg-base50 pb-4 pt-4 hover:bg-white">
            <p className="pl-6 font-base text-base-m font-semibold hover:bg-white">
              Search for "{searchText.trim()}"
            </p>
          </div>
        </AccessibleLink>
      )}
    </>
  );
};

export const SearchResults = React.memo(function SearchResults({
  artistResults,
  releaseResults,
  playlistResults,
  collectorResults,
  isLoading,
  searchText,
  inputText,
  debouncedInputText,
  onRowClick,
  onSearchClick,
  onTouchStart,
  isMobile,
}: {
  artistResults: SearchResultArtistFragment[];
  releaseResults: SearchResultReleaseFragment[];
  playlistResults: SearchResultShelfFragment[];
  collectorResults: SearchResultCollectorFragment[];
  isLoading: boolean;
  searchText: string;
  inputText: string;
  debouncedInputText: string;
  onRowClick: () => void;
  onSearchClick: () => void;
  onTouchStart?: () => void;
  isMobile?: boolean;
}) {
  const { height } = useVisualViewport({
    delayDebounce: 0,
  });

  const { activeTrackId } = useAudioController();

  const totalResults =
    releaseResults.length + artistResults.length + playlistResults.length + collectorResults.length;

  const shouldShowLoadingState =
    isLoading &&
    // To avoid stutter while typing
    debouncedInputText === inputText;

  const history = useSearchHistoryStore();

  const isSearchTextEmpty = searchText === '' || inputText === '';

  const results = React.useMemo(() => {
    const [historyResults, releases, artists, playlists, collectors] = splitSlots({
      slots,
      lists: [
        isSearchTextEmpty ? history : [],
        releaseResults,
        artistResults,
        playlistResults,
        collectorResults,
      ],
    });

    return {
      history: historyResults,
      releases,
      artists,
      playlists,
      collectors,
    };
  }, [
    history,
    releaseResults,
    artistResults,
    playlistResults,
    collectorResults,
    isSearchTextEmpty,
  ]);

  const resultsContent = React.useMemo(() => {
    return [
      results.history.length ? (
        <SearchRow key="history" header="Recent" isMobile={isMobile}>
          {results.history.map(entity => {
            return <SearchHistoryRow key={entity.id} entity={entity} onRowClick={onRowClick} />;
          })}
        </SearchRow>
      ) : null,
      results.releases.length ? (
        <SearchRow key="releases" header="Releases" isMobile={isMobile}>
          {results.releases.map(release => {
            return <ReleaseRow key={release.id} release={release} onRowClick={onRowClick} />;
          })}
        </SearchRow>
      ) : null,
      results.artists.length ? (
        <SearchRow key="artists" header="Artists" isMobile={isMobile}>
          {results.artists.map(artist => {
            return <ArtistRow key={artist.id} artist={artist} onRowClick={onRowClick} />;
          })}
        </SearchRow>
      ) : null,
      results.playlists.length ? (
        <SearchRow key="playlists" header="Playlists" isMobile={isMobile}>
          {results.playlists.map(playlist => {
            return <PlaylistRow key={playlist.id} playlist={playlist} onRowClick={onRowClick} />;
          })}
        </SearchRow>
      ) : null,
      results.collectors.length ? (
        <SearchRow key="collectors" header="Collectors" isMobile={isMobile}>
          {results.collectors.map(collector => {
            return (
              <CollectorRow key={collector.id} collector={collector} onRowClick={onRowClick} />
            );
          })}
        </SearchRow>
      ) : null,
    ].filter((value): value is NonNullable<typeof value> => value != null);
  }, [
    isMobile,
    onRowClick,
    results.artists,
    results.collectors,
    results.history,
    results.playlists,
    results.releases,
  ]);

  const shouldShowNoResults = !shouldShowLoadingState && totalResults === 0 && !isLoading;

  const searchResultsContent = React.useMemo(() => {
    return (
      <SearchResultsContainer onTouchStart={onTouchStart} loadingState={shouldShowLoadingState}>
        {shouldShowLoadingState ? (
          <LoadingContainer>
            <LoadingBars height={undefined} />
          </LoadingContainer>
        ) : shouldShowNoResults ? (
          <SearchForRow searchText={searchText} onSearchClick={onSearchClick} />
        ) : totalResults ? (
          <div className="mb-3">
            {searchText !== '' && (
              <SearchForRow searchText={searchText} onSearchClick={onSearchClick} />
            )}
            {resultsContent.map((value, index) => {
              return index === 0 ? (
                <div className="pt-2">{value}</div>
              ) : (
                <React.Fragment key={index}>
                  <DividerContainer>{isMobile ? null : <SearchDivider />}</DividerContainer>
                  {value}
                </React.Fragment>
              );
            })}
          </div>
        ) : null}
      </SearchResultsContainer>
    );
  }, [
    isMobile,
    onTouchStart,
    resultsContent,
    shouldShowLoadingState,
    shouldShowNoResults,
    totalResults,
    searchText,
    onSearchClick,
  ]);

  if (isMobile) {
    const substract = activeTrackId
      ? MOBILE_NAV_HEIGHT + MINIMIZED_PLAYER_HEIGHT
      : MOBILE_NAV_HEIGHT + 32;

    return (
      <ScrollLock isActive>
        <ScrollableContainer
          style={{
            maxHeight: `calc(${height}px - ${substract}px - env(safe-area-inset-bottom))`,
            paddingBottom: !!substract
              ? `calc(${substract}px + env(safe-area-inset-bottom))`
              : 'unset',
          }}
        >
          <HeaderContainer>
            <Header>Search</Header>
          </HeaderContainer>
          {searchResultsContent}
        </ScrollableContainer>
      </ScrollLock>
    );
  }

  return (
    <ScrollableContainer
      style={{
        zIndex: '$search',
        height: 'fit-content',
        maxHeight:
          shouldShowLoadingState || shouldShowNoResults || !totalResults
            ? 'fit-content'
            : height - 85,
      }}
    >
      {searchResultsContent}
    </ScrollableContainer>
  );
});

const HeaderContainer = styled('div', {
  width: '100%',
  padding: '0.5rem 1.5rem 0.375rem 1.125rem',
});

const Header = styled('h1', {
  title: 'L',
});
